/**
 * Gestionnaire de notifications push pour les admins FTPE
 * Permet d'activer/désactiver les notifications et de les tester
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Bell, BellOff, BellRing, CheckCircle, AlertTriangle, Send, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

// Convertir la clé VAPID en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationManager() {
  const { getAuthHeader } = useAuth();
  
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkSupport();
    fetchStats();
  }, []);

  const checkSupport = async () => {
    // Vérifier le support des notifications push
    const isSupported = 'serviceWorker' in navigator && 
                       'PushManager' in window && 
                       'Notification' in window;
    
    setSupported(isSupported);
    
    if (isSupported) {
      setPermission(Notification.permission);
      
      // Vérifier si déjà inscrit
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setSubscribed(!!subscription);
      } catch {
      }
    }
    
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ftpe-adhesion/push/stats`, {
        headers: getAuthHeader()
      });
      setStats(res.data.stats);
    } catch {
    }
  };

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      return registration;
    } catch (e) {
      throw e;
    }
  };

  const enableNotifications = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Demander la permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Permission refusée. Activez les notifications dans les paramètres du navigateur.');
        setLoading(false);
        return;
      }
      
      // Enregistrer le service worker
      const registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;
      
      // Récupérer la clé VAPID depuis le backend si non disponible
      let vapidKey = VAPID_PUBLIC_KEY;
      if (!vapidKey) {
        const keyRes = await axios.get(`${BACKEND_URL}/api/ftpe-adhesion/push/vapid-key`);
        vapidKey = keyRes.data.publicKey;
      }
      
      // S'inscrire aux notifications push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
      
      // Envoyer la souscription au backend
      await axios.post(
        `${BACKEND_URL}/api/ftpe-adhesion/push/subscribe`,
        { subscription: subscription.toJSON() },
        { headers: getAuthHeader() }
      );
      
      setSubscribed(true);
      fetchStats();
      
    } catch (e) {
      setError(e.message || 'Erreur lors de l\'activation des notifications');
    } finally {
      setLoading(false);
    }
  };

  const disableNotifications = async () => {
    setLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Désinscrire du backend
        await axios.delete(
          `${BACKEND_URL}/api/ftpe-adhesion/push/unsubscribe`,
          { 
            headers: getAuthHeader(),
            data: { endpoint: subscription.endpoint }
          }
        );
        
        // Désinscrire localement
        await subscription.unsubscribe();
      }
      
      setSubscribed(false);
      fetchStats();
      
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const testNotification = async () => {
    setTesting(true);
    
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ftpe-adhesion/push/test`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (res.data.success) {
        // Notification envoyée avec succès
      } else {
        setError(res.data.message || 'Échec du test');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="py-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Notifications non supportées</p>
              <p className="text-sm text-amber-700">
                Votre navigateur ne supporte pas les notifications push.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${subscribed ? 'bg-emerald-100' : 'bg-gray-100'}`}>
              {subscribed ? (
                <BellRing className="w-5 h-5 text-emerald-600" />
              ) : (
                <BellOff className="w-5 h-5 text-gray-500" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg">Notifications Push</CardTitle>
              <CardDescription>
                Recevez une alerte quand une nouvelle adhésion FTPE est soumise
              </CardDescription>
            </div>
          </div>
          <Badge className={subscribed ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}>
            {subscribed ? 'Activées' : 'Désactivées'}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Erreur */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        
        {/* Contrôles */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-gray-500" />
            <div>
              <p className="font-medium text-gray-900">Notifications d'adhésion</p>
              <p className="text-sm text-gray-500">
                {permission === 'denied' ? 
                  'Permission refusée - Modifiez dans les paramètres du navigateur' :
                  'Alertes pour nouvelles adhésions FTPE'
                }
              </p>
            </div>
          </div>
          
          <Switch
            checked={subscribed}
            onCheckedChange={(checked) => checked ? enableNotifications() : disableNotifications()}
            disabled={loading || permission === 'denied'}
          />
        </div>
        
        {/* Statistiques */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 py-3">
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.total_subscriptions}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-600">{stats.active_subscriptions}</p>
              <p className="text-xs text-gray-500">Actives</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.admin_subscriptions}</p>
              <p className="text-xs text-gray-500">Admins</p>
            </div>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={testNotification}
            disabled={!subscribed || testing}
          >
            {testing ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Tester
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStats}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
        
        {/* Info */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 text-blue-700 rounded-lg text-sm">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Comment ça marche ?</p>
            <p className="text-blue-600">
              Quand un membre soumet une nouvelle demande d'adhésion FTPE, 
              tous les admins avec les notifications activées recevront une alerte instantanée.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
